import { z } from "zod";

// Schéma pour les custom attributes dans la réponse /me
export const userCustomAttributeSchema = z.object({
  attribute_id: z.number(),
  attribute_name: z.string(),
  attribute_type: z.string(),
  value_type: z.string(),
  // Pour les champs text/date/number (value_type: "free_input")
  free_value: z.string().optional(),
  // Pour les champs select/scale (value_type: "predefined") - number
  // Pour les champs multiselect - string (comma-separated IDs like "123,456,789")
  value_id: z.union([z.number(), z.string()]).optional(),
  value_name: z.string().optional(),
  locked: z.boolean().optional(),
});

export const pendingEmailChangeSchema = z.object({
  new_email: z.string(),
  expires_at: z.string(),
});

export const genderEnum = z.enum(["Monsieur", "Madame"]);
export const genderNumberEnum = z.enum(["1", "2"]);

export const convertGenderNumberToString = (genderNumber: number): "Monsieur" | "Madame" => {
  return genderNumber === 1 ? "Monsieur" : "Madame";
};

// Fonction utilitaire pour extraire la valeur d'un custom attribute
export const getCustomAttributeValue = (attr: UserCustomAttribute): string => {
  // Pour les selects/multiselect/scale (predefined), le formulaire doit recevoir l'id de valeur en string
  if (attr.value_type === "predefined") {
    // Multiselect: value_id est déjà une string comma-separated (ex: "123,456,789")
    if (typeof attr.value_id === "string") {
      return attr.value_id;
    }
    if (typeof attr.value_id === "number") {
      return String(attr.value_id);
    }
    // Fallback très conservateur: renvoyer le label si pas d'id
    return attr.value_name || "";
  }
  // Pour les autres types, renvoyer la valeur libre
  return attr.free_value || "";
};

export const getUserSchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  gender: z.union([z.number().min(1).max(2), z.enum(["Monsieur", "Madame"])]),
  avatar: z.union([z.object({ url: z.string() }), z.string()]),
  last_name: z.string(),
  first_name: z.string(),
  email: z.email(),
  cell_phone_number: z.string(),
  dialing_code: z.string(),
  address_line1: z.string(),
  address_line2: z.string(),
  zip_code: z.string(),
  city: z.string(),
  site: z.string(),
  iban: z.string().optional(),
  bic: z.string().optional(),
  prioritized_voucher_usage_enabled: z.boolean().optional(),
  custom_attribute_locked: z.boolean().optional(),
  granular_lock: z.boolean().optional(),
  email_change_allowed: z.boolean().optional(),
  pending_email_change: pendingEmailChangeSchema.nullable().optional(),
  custom_attributes: z.array(userCustomAttributeSchema).optional(),
});

export const userSchema = z.object({
  id: z.string().optional(),
  organization_id: z.string().optional(),
  gender: z.union([z.number().min(1).max(2), z.enum(["Monsieur", "Madame"]), z.literal("")]).optional(),
  avatar: z.union([z.object({ url: z.string() }), z.string()]).optional(),
  last_name: z.string().optional(),
  first_name: z.string().optional(),
  email: z.email("Format d'email invalide").optional(),
  cell_phone_number: z.string().optional(),
  dialing_code: z.string().optional(),
  // ADM-816 : l'API exige address_line1 >= 5 caractères si elle est renseignée
  // (nil/vide reste accepté). Aligné sur la règle back (DeliveryAddressValidator / Treezor).
  address_line1: z.string()
    .max(35, "L'adresse ligne 1 ne peut pas dépasser 35 caractères")
    .refine((val) => val === "" || val.length >= 5, "L'adresse doit contenir au moins 5 caractères")
    .optional(),
  address_line2: z.string().max(35, "L'adresse ligne 2 ne peut pas dépasser 35 caractères").optional(),
  zip_code: z.string()
    .refine(
      (val) => val === "" || /^\d{5}$/.test(val),
      "Le code postal doit contenir 5 chiffres",
    )
    .optional(),
  city: z.string().optional(),
  site: z.string().optional(),
  organization_site_id: z.string().optional(),
  allow_photo_use: z.boolean().optional(),
  allow_show_cell_phone: z.boolean().optional(),
  allow_use_cell_phone: z.boolean().optional(),
  allow_email_from_cse: z.boolean().optional(),
  allow_email_from_yeati: z.boolean().optional(),
  allow_functional_emails: z.boolean().optional(),
  allow_cse_emails: z.boolean().optional(),
  allow_email_notifications: z.boolean().optional(),
  allow_push_notifications: z.boolean().optional(),
  notification_chat_email: z.boolean().optional(),
  allow_purchase_points: z.boolean().optional(),
  email_change_allowed: z.boolean().optional(),
  pending_email_change: pendingEmailChangeSchema.nullable().optional(),
  custom_attributes: z.array(userCustomAttributeSchema).optional(),
}).refine((data) => {
  if (data.cell_phone_number && data.dialing_code === "+33") {
    return data.cell_phone_number.length >= 9 && data.cell_phone_number.length <= 10;
  }
  return true;
}, {
  message: "Le numéro de téléphone doit être valide",
  path: ["phone"],
});

export const userResponseSchema = z.object({ employee: getUserSchema });

export type UserCustomAttribute = z.infer<typeof userCustomAttributeSchema>;
export type UpdateUserData = z.infer<typeof userSchema>;
export type UpdateUserApiPayload = { employee: UpdateUserData };
